import { useCallback, useEffect, useState } from "react";
import { flushSync } from "react-dom";

/**
 * Light/dark theme for the app chrome (design handoff ADDENDUM-8 §3).
 *
 * The only thing this does is decide the value of `data-theme` on <html>.
 * Everything visual follows from `src/theme/theme.css` — components author
 * `var(--token)` and never branch on the mode.
 *
 * Resolution order, per the addendum:
 *   1. an explicit choice the user has made before, from localStorage
 *   2. otherwise `prefers-color-scheme`, tracked live — but only for as long as
 *      the user has made no explicit choice. Once they toggle, the OS stops
 *      speaking for them.
 *
 * The stored value is `"light" | "dark"`; absent means "no explicit choice".
 */

const STORAGE_KEY = "ce-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";

/** The stored explicit choice, or null if the user has never toggled. */
function storedChoice() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    // Safari in private mode throws on localStorage access. Fall back to the
    // OS preference rather than failing to render.
    return null;
  }
}

function systemTheme() {
  return typeof window !== "undefined" && window.matchMedia(DARK_QUERY).matches ? "dark" : "light";
}

/** The theme to paint on the very first frame, before React mounts. */
export function initialTheme() {
  if (typeof window === "undefined") return "light";
  return storedChoice() || systemTheme();
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/**
 * Flips `data-theme` on <html>.
 *
 * The attribute flip is bracketed by a `ce-theme-swapping` class on <html> for
 * one frame. That is the fix for ADDENDUM-8 §4: colour properties that change
 * by custom-property identity rather than by value never reach their transition
 * endpoint, so anything mid-transition sticks at the previous theme's colour.
 * Killing transitions for the duration of the swap sidesteps it entirely, and
 * is belt-and-braces alongside keeping colour out of the `.lift` transition
 * list in the first place.
 */
function swapAttribute(theme) {
  const root = document.documentElement;
  root.classList.add("ce-theme-swapping");
  root.setAttribute("data-theme", theme);
  // Two frames: one for the attribute to land, one for the new values to
  // resolve, before transitions are allowed to matter again.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => root.classList.remove("ce-theme-swapping"));
  });
}

/**
 * Applies a theme, cross-fading the whole page into it.
 *
 * A bare attribute flip is a hard cut across the viewport, and letting each
 * element tween its own colours instead is worse — hundreds of curves of
 * differing lengths, plus the §4 trap. Neither reads as a transition.
 *
 * So the page moves as one: the flip runs inside a View Transition, and the
 * browser cross-fades a snapshot of the old theme into the new one. Short
 * enough to feel immediate, long enough not to be a cut — the duration and
 * curve live in `theme.css` beside the rest of the transition rules.
 *
 * Falls back to the plain flip where there are no View Transitions (Firefox,
 * older Safari) and under `prefers-reduced-motion`.
 *
 * @param {"light"|"dark"} theme
 * @param {Function} [commit] - Run alongside the flip, inside the transition,
 *   so React's own re-render (the button's glyph) is part of the same snapshot
 *   instead of popping once the fade has finished.
 */
function applyTheme(theme, commit) {
  const root = document.documentElement;
  if (root.getAttribute("data-theme") === theme) return;

  const swap = () => {
    swapAttribute(theme);
    if (commit) commit();
  };

  if (!document.startViewTransition || prefersReducedMotion()) {
    swap();
    return;
  }

  // A transition can be skipped (a second click mid-fade, a hidden tab). The
  // attribute is already flipped by then, so there is nothing to recover.
  document.startViewTransition(swap).finished.catch(() => {});
}

/**
 * @returns {{theme: "light"|"dark", isDark: boolean, setTheme: Function, toggleTheme: Function}}
 */
export function useTheme() {
  const [theme, setThemeState] = useState(initialTheme);

  useEffect(() => { applyTheme(theme); }, [theme]);

  // Follow the OS only while the user has made no explicit choice.
  useEffect(() => {
    const mq = window.matchMedia(DARK_QUERY);
    const onChange = (e) => {
      if (storedChoice()) return;
      setThemeState(e.matches ? "dark" : "light");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  // Other tabs, and the other mount of this hook on the same page — the header
  // and the selector both call it, and they have to agree.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setThemeState(e.newValue === "dark" ? "dark" : "light");
    };
    const onLocal = (e) => setThemeState(e.detail);
    window.addEventListener("storage", onStorage);
    window.addEventListener("ce-theme-change", onLocal);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("ce-theme-change", onLocal);
    };
  }, []);

  /** @param {"light"|"dark"} next */
  const setTheme = useCallback((next) => {
    try { window.localStorage.setItem(STORAGE_KEY, next); } catch { /* private mode */ }
    // Flip here rather than leaving it to the effect above: the View Transition
    // has to snapshot the page as it looks *now*, from the click. The React
    // updates ride inside it — `storage` does not fire in the tab that wrote
    // it, so the sibling mounts of this hook need their own signal.
    applyTheme(next, () => {
      flushSync(() => {
        setThemeState(next);
        window.dispatchEvent(new CustomEvent("ce-theme-change", { detail: next }));
      });
    });
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(document.documentElement.getAttribute("data-theme") === "dark" ? "light" : "dark");
  }, [setTheme]);

  return { theme, isDark: theme === "dark", setTheme, toggleTheme };
}

export default useTheme;
